import { Int } from '@adornis/baseql/baseqlTypes.js';
import { registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { watchEntireCollection } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { MailEntity } from '@adornis/mail-client/mail-entity.js';
import { AdornisUser } from '@adornis/users/db/a-user.js';
import { debounceTime, merge, switchMap } from 'rxjs';
import { EnrichedMailEntity } from '../db/EnrichedMailEntity.js';
import { MailProcessing } from '../features/information-extraction/db/MailExtractionResult.js';
import { MailboxFilter } from '../db/MailboxFilter.js';
import { getCurrentUserID } from './currentUserID.js';

/**
 * Subscription returning mail entities enriched with their joined MailProcessing
 * documents (sapNumber, sentToSap, extractionResult.requestID, notes).
 *
 * The underlying Mongo documents live in the `mail` collection and are stored
 * with `_class: 'MailEntity'`. We rewrite `_class` to `'EnrichedMailEntity'`
 * in-memory so (a) the GraphQL schema builder serializes the documents with
 * the `mailProcessings` field picked up by EnrichedMailEntity and (b) the
 * client's `applyConstructor` upgrades them to EnrichedMailEntity instances.
 *
 * Both the `mail` and `mail-processings` collections are watched so changes
 * in either (e.g. a SAP number update) trigger a live refresh of the table.
 */
/** Looks up a user's e-mail address by id (used to resolve me-mode server-side). */
const getUserEmail = async (userID: string): Promise<string | undefined> => {
  const users = await getRawCollection<EntityData<AdornisUser>>(AdornisUser._collectionName);
  const user = await users.findOne({ _id: userID }, { projection: { email: 1 } });
  return user?.email ?? undefined;
};

export const subscribeEnrichedMailEntitiesPaginatedFiltered = registerSubscription({
  type: () => [EnrichedMailEntity],
  operationName: 'subscribeEnrichedMailEntitiesPaginatedFiltered',
  resolve:
    (skip: number, limit: number, filter: AdornisFilter) =>
    (_gqlFields: BaseQLSelectionSet<EnrichedMailEntity>) => {
      // Capture the user id synchronously while the request context (ALS) is
      // still available — the switchMap callback below runs detached from it.
      const mailboxFilter = filter instanceof MailboxFilter ? filter : null;
      const userID = mailboxFilter?.mineOnly ? getCurrentUserID() : undefined;

      return merge(
        watchEntireCollection(MailEntity._collectionName),
        watchEntireCollection(MailProcessing._collectionName),
      ).pipe(
        // Each collection change maps to `void`, so a value-based dedupe
        // (distinctUntilChanged) would swallow every update after the first.
        // Debounce instead: push on every change, coalescing bursts like a bulk
        // archive (one updateMany → many change events) into a single refresh.
        debounceTime(100),
        switchMap(async () => {
          // Me-mode: resolve the logged-in user's e-mail server-side and inject
          // it as the sender filter, so the result cannot be tampered with from
          // the client and stays correct even if the client e-mail is stale.
          if (mailboxFilter?.mineOnly) {
            mailboxFilter.senderAddress = userID ? await getUserEmail(userID) : '\0__no_user__';
          }

          const rawMailEntityCollection = await getRawCollection<EnrichedMailEntity>(MailEntity._collectionName);
          // Honour the pagination window the client requested (usePagination passes
          // skip=0 and a growing limit). Without this the resolver returned the whole
          // mailbox on every change, so the list cost — and any detail-page open that
          // writes a MailProcessing and re-triggers this watch — scaled with mailbox size.
          const pipeline: any[] = filter.getMongoAggregationFilter();
          if (skip > 0) pipeline.push({ $skip: skip });
          if (limit > 0) pipeline.push({ $limit: limit });
          const mailEntities = await rawMailEntityCollection.aggregate<EnrichedMailEntity>(pipeline).toArray();
          for (const doc of mailEntities) {
            (doc as any)._class = EnrichedMailEntity._class;
          }
          return mailEntities;
        }),
      );
    },
  options: {},
  params: [
    { type: () => Int, name: 'skip' },
    { type: () => Int, name: 'limit' },
    { type: () => AdornisFilter, name: 'filter' },
  ],
});
